import {
  Button,
  column,
  Css,
  emptyCell,
  FilterDefs,
  Filters,
  GridColumn,
  GridDataRow,
  GridTable,
  GridTableApi,
  ModalBody,
  ModalFooter,
  ModalHeader,
  multiFilter,
  MultiSelectField,
  selectColumn,
  simpleDataRows,
  SimpleHeaderAndData,
  Tag,
  useComputed,
  useFilter,
  useGridTableApi,
  useModal,
} from "@homebound/beam";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { SearchBox } from "src/components";
import { ArchivedTag } from "src/components/ArchivedTag";
import { useGlobalOptionTagsFilter } from "src/components/autoPopulateFilters/useGlobalOptionTagsFilter";
import { useGlobalOptionTypes } from "src/components/autoPopulateSelects/useGlobalOptionTypes";
import { GlobalOptionStatus, SaveReadyPlanOptionInput } from "src/generated/graphqlTypes";
import { useNavigateToRpav } from "src/hooks/useNavigateToRpav";
import { useSavePlanPackageOptionsV2Mutation } from "src/routes/libraries/plan-package-v2/components/OptionsTab.generated";
import { queryResult } from "src/utils";
import {
  AddOptionsModalV2_GlobalOptionFragment,
  AddOptionsModalV2Query,
  useAddOptionsModalV2_GlobalOptionsQuery,
  useAddOptionsModalV2Query,
} from "./AddOptionsModal.generated";

type AddOptionsModalProps = { planPackageId: string; versionId: string };
type CatalogFilter = { type?: string[]; tags?: string[] };
type Row = SimpleHeaderAndData<AddOptionsModalV2_GlobalOptionFragment>;
type PlanPackage = AddOptionsModalV2Query["planPackage"];
type ExistingRpo = NonNullable<PlanPackage>["options"][number];

export function AddOptionsModal(props: AddOptionsModalProps) {
  const { planPackageId, versionId } = props;
  const { closeModal } = useModal();
  const navigateToRpav = useNavigateToRpav();
  const [save] = useSavePlanPackageOptionsV2Mutation();
  const api = useGridTableApi<Row>();
  const [search, setSearch] = useState<string>();
  // Locations picked per global option, where null means no location
  const [locationsByGo, setLocationsByGo] = useState<Record<string, Array<string | null>>>({});
  const gots = useGlobalOptionTypes({ isElevation: false, version: [2] });
  const optionTypes = useMemo(() => gots?.filter((got) => got.globalOptionsCount > 0) ?? [], [gots]);
  const tagsFilter = useGlobalOptionTagsFilter();
  const filterDefs: FilterDefs<CatalogFilter> = useMemo(
    () => ({
      type: multiFilter({
        label: "Type",
        options: optionTypes,
        getOptionLabel: ({ name }) => name,
        getOptionValue: ({ id }) => id,
      }),
      tags: tagsFilter,
    }),
    [optionTypes, tagsFilter],
  );
  const { filter, setFilter } = useFilter({ filterDefs });
  const ppQuery = useAddOptionsModalV2Query({ variables: { id: planPackageId, versionId }, skip: optionTypes.isEmpty });
  const planPackage = ppQuery.data?.planPackage;
  const goQuery = useAddOptionsModalV2_GlobalOptionsQuery({
    variables: {
      filter: {
        status: [GlobalOptionStatus.Active],
        // Include the catalog options plus any options scoped to the plan's design packages
        readyPlan: [null, ...(planPackage?.designPackages.map((dp) => dp.id) ?? [])],
        tags: filter.tags,
        type: filter.type?.nonEmpty ? filter.type : optionTypes.map(({ id }) => id),
      },
    },
    skip: optionTypes.isEmpty || ppQuery.loading,
  });
  // Key existing options by pair, actives last so they win over legacy archived duplicates
  const existingRpos = useMemo(
    () =>
      new Map(
        (planPackage?.options ?? [])
          .sortBy((rpo) => Number(rpo.active))
          .map((rpo) => [rpoKey(rpo.globalOption.id, rpo.readyPlanLocation?.id ?? null), rpo]),
      ),
    [planPackage],
  );
  const selectedRows = useComputed(() => api.getSelectedRows("data"), [api]);

  // Default newly checked rows to no location
  useEffect(() => {
    selectedRows.forEach(({ data }) => {
      if ((locationsByGo[data.id] ?? []).isEmpty) {
        setLocationsByGo((prev) => ({ ...prev, [data.id]: [null] }));
      }
    });
  }, [selectedRows, locationsByGo]);

  // Map each selected option + location pair to a create, or reactivating its archived option
  const optionInputs = selectedRows.flatMap(({ data }) =>
    (locationsByGo[data.id] ?? []).flatMap((rplId): SaveReadyPlanOptionInput[] => {
      const existing = existingRpos.get(rpoKey(data.id, rplId));
      if (existing?.active) return [];
      if (existing) return [{ id: existing.id, active: true }];
      return [{ globalOptionId: data.id, ...(rplId && { readyPlanLocationId: rplId }) }];
    }),
  );

  async function onAdd() {
    const { data } = await save({ variables: { input: { id: planPackageId, options: optionInputs } } });
    closeModal();
    const newVersionId = data?.savePlanPackage.planPackage.version.id;
    // Adding options on a published version forks a new draft, so navigate to it
    if (newVersionId && newVersionId !== planPackage?.version.id) navigateToRpav(newVersionId);
  }

  const columns = useMemo(
    () => createColumns(planPackage, existingRpos, locationsByGo, setLocationsByGo, api),
    [planPackage, existingRpos, locationsByGo, api],
  );

  return (
    <>
      <ModalHeader>Add Options</ModalHeader>
      <ModalBody>
        <div css={Css.df.aic.gap1.pb2.$}>
          <SearchBox onSearch={setSearch} clearable updateQueryString={false} />
          <Filters filter={filter} filterDefs={filterDefs} onChange={setFilter} />
          {Object.keys(filter).nonEmpty && <Button label="Clear" variant="text" onClick={() => setFilter({})} />}
        </div>
        {queryResult(goQuery, ({ globalOptions }) => (
          <div css={Css.hPx(550).$}>
            <GridTable
              api={api}
              columns={columns}
              rows={createRows(globalOptions)}
              filter={search}
              as="virtual"
              fallbackMessage="No options found"
            />
          </div>
        ))}
      </ModalBody>
      <ModalFooter>
        <Button label="Cancel" variant="tertiary" onClick={closeModal} />
        <Button
          data-testid="add"
          label={
            optionInputs.isEmpty
              ? "Add Options"
              : `Add ${optionInputs.length} Option${optionInputs.length === 1 ? "" : "s"}`
          }
          disabled={optionInputs.isEmpty}
          onClick={onAdd}
        />
      </ModalFooter>
    </>
  );
}

function createRows(globalOptions: AddOptionsModalV2_GlobalOptionFragment[]): GridDataRow<Row>[] {
  // Most recently used options first, like the old stepper's default sort
  return simpleDataRows(globalOptions.sortBy((go) => -(go.lastUsedAt?.epochMilliseconds ?? 0)));
}

function createColumns(
  planPackage: PlanPackage | undefined,
  existingRpos: Map<string, ExistingRpo>,
  locationsByGo: Record<string, Array<string | null>>,
  setLocationsByGo: Dispatch<SetStateAction<Record<string, Array<string | null>>>>,
  api: GridTableApi<Row>,
): GridColumn<Row>[] {
  const roomLocations = planPackage?.roomLocations ?? [];
  return [
    selectColumn<Row>({ header: emptyCell }),
    column<Row>({ header: "Option Code", data: ({ code }) => code, w: "160px" }),
    column<Row>({ header: "Name", data: ({ displayName }) => displayName, mw: "200px" }),
    column<Row>({
      header: "Type",
      data: ({ type }) => ({ content: <Tag variant="secondary" text={type.name} />, value: type.name }),
      w: "120px",
    }),
    column<Row>({
      header: "Location",
      data: (go, { row }) => (
        <MultiSelectField<{ id: string | null; name: string }, string | null>
          label="Locations"
          labelStyle="hidden"
          disabled={!go.type.allowsLocations && `Options of type ${go.type.name} do not allow locations`}
          options={[{ id: null, name: "None" }, ...roomLocations]}
          values={locationsByGo[go.id] ?? []}
          getOptionLabel={({ name }) => name}
          getOptionValue={({ id }) => id}
          getOptionMenuLabel={({ id, name }) => {
            // Tag locations where this option was previously removed, i.e. adding it back reactivates it
            const existing = existingRpos.get(rpoKey(go.id, id));
            return <ArchivedTag active={!existing || existing.active}>{name}</ArchivedTag>;
          }}
          disabledOptions={roomLocations
            .filter((rpl) => existingRpos.get(rpoKey(go.id, rpl.id))?.active)
            .map((rpl) => ({ value: rpl.id, reason: "Already added" }))}
          onSelect={(values) => {
            setLocationsByGo((prev) => ({ ...prev, [go.id]: values }));
            api.selectRow(row.id, values.nonEmpty);
          }}
        />
      ),
      w: "200px",
      clientSideSort: false,
    }),
  ];
}

function rpoKey(globalOptionId: string, rplId: string | null): string {
  return `${globalOptionId}-${rplId}`;
}
